/**
 * slateCache — keep "who are we knocking for" readable with no network.
 *
 * Why this exists
 * ---------------
 * The slate rides along on the `ShiftBriefing` payload (plan §8.1.1), which
 * `CanvassView` fetches once when a shift becomes active and holds in React
 * state for the walk. That covers the normal case: the chip never fetches, so
 * losing LTE mid-shift costs nothing.
 *
 * It does not cover a **reload on a dead network**. React state dies with the
 * page; `GET /canvass/shifts/{id}/briefing` then fails; the slate is gone for
 * the rest of the walk, on the exact rural porch where the volunteer most
 * needs it. `canvassOfflineQueue.ts` persists *writes* through this scenario —
 * this is the read half, and it is the same 30 lines minus the transactional
 * ordering that queue needs.
 *
 * Why localStorage and not IndexedDB
 * ----------------------------------
 * Deliberately the opposite call from `canvassOfflineQueue`, and the reasons
 * that pushed the queue to IDB all invert here:
 *   - Size: a slate is a handful of small objects (~1KB), nowhere near the
 *     ~5MB origin cap. The queue can hold photo-attached knocks; this can't.
 *   - Concurrency: the queue's "two tabs corrupt each other" risk comes from
 *     read-modify-write on a list. This is a whole-value overwrite of
 *     server-authoritative data — last write wins is exactly right.
 *   - Sync API: the chip renders from this on first paint. An async read would
 *     mean a flash of no-slate on every reload, which is the failure we're
 *     fixing.
 *
 * This cache is a **fallback, not a source of truth.** A fresh server response
 * always wins and always overwrites. We only read it when the network says no.
 */
import type { CandidateBrief } from '@/types/candidate';

// v2 adds `slate_configured`. Bumped rather than defaulted because a v1 entry
// cannot answer "does this tenant have candidates at all?" — and guessing wrong
// either false-alarms every volunteer or hides a real gap. Old entries are
// simply never found and get swept.
const KEY_PREFIX = 'pcgop.slate.v2.';

/** What we persist per shift. Versioned in the key so a shape change can't
 *  hand a stale reader a payload it will misread — bump `v1` and old entries
 *  are simply never found (and get swept below). */
interface CachedSlate {
  shift_id: number;
  candidates: CandidateBrief[];
  slate_names: string;
  /** See ShiftBriefing.slate_configured — "not configured" vs "misconfigured". */
  slate_configured: boolean;
  cached_at: string; // ISO
}

function keyFor(shiftId: number): string {
  return `${KEY_PREFIX}${shiftId}`;
}

function available(): boolean {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch {
    // Safari private mode / lockdown throws on mere access.
    return false;
  }
}

/**
 * Persist a shift's slate. Called on every successful briefing load.
 *
 * Silently no-ops on failure (quota, private mode). A cache write failing must
 * never break a walk — the in-memory slate is still fine, and the only loss is
 * the reload-offline safety net.
 */
export function cacheSlate(
  shiftId: number,
  candidates: CandidateBrief[],
  slateNames: string,
  slateConfigured: boolean,
): void {
  if (!available()) return;
  try {
    const payload: CachedSlate = {
      shift_id: shiftId,
      candidates,
      slate_names: slateNames,
      slate_configured: slateConfigured,
      cached_at: new Date().toISOString(),
    };
    window.localStorage.setItem(keyFor(shiftId), JSON.stringify(payload));
  } catch {
    /* non-fatal by design — see docstring */
  }
}

/**
 * Read a shift's cached slate, or null if we have nothing.
 *
 * ⚠️ Returning `null` and returning `{candidates: []}` mean different things
 * and callers must keep them apart: null = "we don't know", `[]` = "we know,
 * and the answer is nobody". Collapsing them turns the loud empty state
 * (plan §3.5) back into a silent one.
 */
export function readCachedSlate(
  shiftId: number,
): {
  candidates: CandidateBrief[];
  slate_names: string;
  slate_configured: boolean;
} | null {
  if (!available()) return null;
  try {
    const raw = window.localStorage.getItem(keyFor(shiftId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedSlate;
    if (!parsed || !Array.isArray(parsed.candidates)) return null;
    return {
      candidates: parsed.candidates,
      slate_names: parsed.slate_names || '',
      slate_configured: parsed.slate_configured === true,
    };
  } catch {
    return null;
  }
}

/**
 * Drop one shift's cached slate. Call when a shift ends — the slate is a
 * property of a walk in progress and has no meaning afterward.
 */
export function clearCachedSlate(shiftId: number): void {
  if (!available()) return;
  try {
    window.localStorage.removeItem(keyFor(shiftId));
  } catch {
    /* non-fatal */
  }
}
